import { createTheme } from '@mui/material/styles';

// Shared by MUI and the existing sx styles. Keep foreground and CTA distinct.
export const colors = {
  page: '#08080b',
  surface: '#121216',
  elevated: '#1b1b22',
  button: '#25252e',
  hover: '#343440',
  border: 'rgba(255,255,255,0.12)',
  controlBorder: '#626270',
  text: '#f5f5f5',
  secondary: '#b8b8c2',
  muted: '#a1a1af',
  accent: '#bf87f5',
  cta: '#8b2bd9',
  ctaHover: '#9634df',
  tint: 'rgba(168,85,247,0.12)',
  focus: '#d5afff',
  error: '#ff8a9b',
  warningSurface: '#322719',
  warningBorder: '#a17a39',
  white: '#ffffff',
};

export const catalogColors = {
  bg: colors.surface,
  bgSoft: colors.elevated,
  paper: colors.elevated,
  border: colors.border,
  line: colors.border,
  text: colors.text,
  muted: colors.secondary,
  subtle: colors.muted,
  violet: colors.cta,
  violetSoft: colors.ctaHover,
  violetTint: colors.tint,
  black: colors.button,
  white: colors.elevated,
};

export const headingFont = "'Anton', 'Arial Narrow', sans-serif";
export const uiFont = "'IBM Plex Mono', monospace";
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: colors.accent, contrastText: colors.page },
    secondary: { main: colors.accent },
    background: { default: colors.page, paper: colors.surface },
    text: { primary: colors.text, secondary: colors.secondary, disabled: colors.muted },
    divider: colors.border,
    error: { main: colors.error },
    warning: { main: '#efbf72' },
    success: { main: '#86d8ac' },
    action: { hover: colors.tint, selected: colors.tint, disabled: colors.muted, disabledBackground: colors.elevated },
  },
  shape: { borderRadius: 4 },
  typography: {
    fontFamily: "'IBM Plex Sans', Arial, sans-serif",
    ...Object.fromEntries(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1'].map(variant => [variant, {
      fontFamily: headingFont, fontWeight: 400, letterSpacing: '0.015em',
    }])),
    button: { fontFamily: uiFont, fontSize: '0.75rem', textTransform: 'uppercase' },
    caption: { fontFamily: uiFont }, overline: { fontFamily: uiFont },
  },
  components: {
    MuiCssBaseline: { styleOverrides: {
      ':root': { colorScheme: 'dark' },
      body: { backgroundColor: colors.page },
      'h1, h2': { textTransform: 'uppercase' },
      '::selection': { backgroundColor: colors.cta, color: colors.white },
      ':focus-visible': { outline: `2px solid ${colors.focus}`, outlineOffset: 3 },
      '*': { scrollbarColor: `${colors.controlBorder} ${colors.page}`, scrollbarWidth: 'thin' },
      'input:-webkit-autofill': { WebkitBoxShadow: `0 0 0 100px ${colors.elevated} inset`, WebkitTextFillColor: colors.text, caretColor: colors.text },
    } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiButton: { styleOverrides: { root: {
      fontWeight: 500,
      borderRadius: 4,
      minHeight: 44,
      '&.Mui-focusVisible': { outline: `2px solid ${colors.focus}`, outlineOffset: 3 },
    }, containedPrimary: { backgroundColor: colors.cta, color: colors.white, '&:hover': { backgroundColor: colors.ctaHover } } } },
    MuiIconButton: { styleOverrides: { root: { color: colors.text, '&.Mui-focusVisible': { outline: `2px solid ${colors.focus}`, outlineOffset: 3 } } } },
    MuiOutlinedInput: { styleOverrides: { root: {
      backgroundColor: colors.elevated,
      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.controlBorder },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.secondary },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: colors.error },
    }, input: { '&::placeholder': { color: colors.secondary, opacity: 1 } } } },
    MuiInputLabel: { styleOverrides: { root: { color: colors.secondary } } },
    MuiChip: { styleOverrides: { root: { backgroundColor: colors.elevated, borderRadius: 4, fontFamily: uiFont, textTransform: 'uppercase' }, outlined: { borderColor: colors.controlBorder } } },
    MuiTableCell: { styleOverrides: { root: { borderColor: colors.border }, head: { color: colors.text, fontWeight: 800, backgroundColor: colors.elevated } } },
    MuiTableRow: { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { backgroundColor: colors.tint } } } },
    MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: colors.hover, color: colors.text, border: `1px solid ${colors.controlBorder}` }, arrow: { color: colors.hover } } },
    MuiDialog: { styleOverrides: { paper: { border: `1px solid ${colors.border}` } } },
  },
});

export default theme;
